import { useEffect, useState } from "react";
import InfiniteScroll from "../../../components/ui/InfiniteScroll";
import { WalletHistoryRequest } from "../models/request/walletHistoryRequest";
import { WalletHistoryResponse } from "../models/response/walletHistoryResponse";
import walletHistoryService from "../services/wallethistory.service";
import {
  formatDate,
  formatUtcToLocalDateTime
} from "../../../utils/dateTimeHelper";
import { toastService as toast, toastService } from "../../../components/ui/toast/toast.service";
import TransactionCard from "../../../components/ui/TransactionCard";
import { DateRangePicker } from "../../../components/ui/inputs";
import localization from "../../../config/localization";
import { LastWithdrawalResponse } from "../models/response/lastWithdrawalResponse";
import { Button } from "../../../components/ui";
import { ConfirmationModal } from "../../../components/ui/modals";
import { useAppDispatch } from "../../../store/hooks";
import { changeLoaderState } from "../../../reducers/appStateReducer";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { copyToClipboard } from "../../../utils/clipboard";
import { AccessMode } from "../../../models/constants/accessMode";
import userDeviceStore from "../../../store/device.store";

const formatSignedAmount = (amount: number) => {
  const sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
  return `${sign}${Math.abs(amount).toFixed(2)}₹`;
};

const getWalletIcon = (title: string) => {
  if (title?.includes("Deposit")) return "/icons/ic-exchange.svg";
  if (title?.includes("Withdraw")) return "/icons/ic-withdraw.svg";
  if (title?.includes("Bonus")) return "/icons/ic-bonus.svg";
  return "/icons/ic-wallet.svg";
};

export function WalletHistory() {
  const PAGE_SIZE = 20;
  const dispatch = useAppDispatch();

  const [data, setData] = useState<WalletHistoryResponse | null>(null);
  const [pageNo, setPageNo] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastWithdrawal, setLastWithdrawal] = useState<LastWithdrawalResponse | null>(null);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [openRowIds, setOpenRowIds] = useState<string[]>([]);

  const [filters, setFilters] = useState<WalletHistoryRequest>({
    pageNo: 0,
    pageSize: PAGE_SIZE,
    fromDate: null,
    toDate: null,
  });

  const [tempFrom, setTempFrom] = useState<string | null>(null);
  const [tempTo, setTempTo] = useState<string | null>(null);

  const handleOpenRowIds = (itemId: string, isOpen: boolean) => {
    setOpenRowIds((prev) =>
      isOpen ? [...prev, itemId] : prev.filter((id) => id !== itemId)
    );
  };


  const handleCopy = async (text: string) => {
    let accessMode = userDeviceStore.getState().accessModeSource.sourceType ?? AccessMode.Web;
    await copyToClipboard(accessMode, text, {
      onSuccess: () => {
        toastService.success(localization.success_copied);
      },
      onError: () => {
        toastService.error(localization.failed_to_copy_text);
      },
    });
  };

  const fetchWalletHistory = async (reset = false, appliedFilters = filters) => {
    if (loadingMore && !reset) return;

    if (reset) {
      setLoading(true);
      dispatch(changeLoaderState(true));
    } else {
      setLoadingMore(true);
    }

    const response = await walletHistoryService.getWalletHistory({
      ...appliedFilters,
      pageNo: reset ? 0 : pageNo,
      pageSize: PAGE_SIZE,
    });

    if (response) {
      setData((prev) =>
        reset || !prev
          ? response
          : { ...prev, details: [...prev.details, ...response.details] }
      );

      if (response.details.length < PAGE_SIZE) {
        setHasMore(false);
      }
    }

    setPageNo((p) => (reset ? 1 : p + 1));
    setLoading(false);
    setLoadingMore(false);
    if (reset) {
      dispatch(changeLoaderState(false));
    }
  };

  const fetchLastWithdrawal = async () => {
    const res = await walletHistoryService.getWithdrawalLastRequest();
    if (res) {
      setLastWithdrawal(res.data);
    }
  };

  const applyDateFilter = async (startDate: string | null, endDate: string | null) => {
    const updated = {
      ...filters,
      fromDate: startDate,
      toDate: endDate,
    };

    setFilters(updated);
    setOpenRowIds([]);
    setPageNo(0);
    setHasMore(true);
    setData(null);

    await fetchWalletHistory(true, updated);
    await fetchLastWithdrawal();
  };

  const clearDateFilter = async () => {
    setTempFrom(null);
    setTempTo(null);
    const updated = {
      ...filters,
      fromDate: null,
      toDate: null,
    };

    setFilters(updated);
    setOpenRowIds([]);
    setPageNo(0);
    setHasMore(true);
    setData(null);

    await fetchWalletHistory(true, updated);
    await fetchLastWithdrawal();
  };

  useEffect(() => {
    fetchWalletHistory(true);
    fetchLastWithdrawal();
  }, []);

  const canShowRevert = (item: any) => {
    if (!lastWithdrawal?.isrevertallowed) return false;
    if (!item.userwithdrawstatus) return false;

    if (
      item.userwithdrawstatus === "Approved" ||
      item.userwithdrawstatus === "Rejected" ||
      item.userwithdrawstatus === "Reverted" ||
      item.userwithdrawstatus === "Hold" ||
      item.userwithdrawstatus === "Processing"
    ) {
      return false;
    }
    return true;
  };

  const handleWithdrawRevert = async () => {
    if (!lastWithdrawal) return;

    setReverting(true);
    var cancelResponse = await walletHistoryService.cancelWithdraw({
      id: lastWithdrawal.id,
      status: lastWithdrawal.status,
      isDragon: lastWithdrawal.isDragon,
      dragonTransactionId: lastWithdrawal.pgtransactionid,
    });
    if (cancelResponse.isSuccess) {
      toast.success(localization.getString(cancelResponse.message));
    }
    else {
      toast.error(localization.getString(cancelResponse.message));
    }
    setShowRevertModal(false);
    setPageNo(0);
    setHasMore(true);
    setData(null);

    await fetchWalletHistory(true);
    await fetchLastWithdrawal();
    setReverting(false);
  };
  return (
    <div className="p-4">
      <div className="wallet-date-wrapper">
        {!filters.fromDate || !filters.toDate ? (
          <DateRangePicker
            id="wallet-history-date-range"
            placeholder={localization.select_date}
            startDate={tempFrom}
            endDate={tempTo}
            onRangeChange={(start, end) => {
              setTempFrom(start);
              setTempTo(end);
            }}
            onApply={(start, end) => {
              applyDateFilter(start, end);
            }}
            onClear={clearDateFilter}
            disableFutureDates={true}
          />
        ) : (
          <div className="wallet-date-pill">
            <img
              src="/icons/ic-daterange.svg"
              className="wallet-date-icon"
              alt="daterange"
            />
            <span className="wallet-date-text">
              {formatDate(filters.fromDate)}
            </span>
            <img
              src={"/icons/ic-right-arrow.svg"}
              alt="right-arrow"
              className="size-4"
            />
            <span className="wallet-date-text">
              {formatDate(filters.toDate)}
            </span>
            <Button className="wallet-date-clear rounded-full" onClick={clearDateFilter}>
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
        )}
      </div>
      {data && (
        <InfiniteScroll
          hasMore={hasMore}
          isLoading={loading || loadingMore}
          onLoadMore={() => fetchWalletHistory()}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4 items-start auto-rows-min">
            {data.details.map((item) => {
              const isOpen = openRowIds.includes(item.id);

              return (
                <TransactionCard
                  key={item.id}
                  onToggleChange={(open) =>
                    handleOpenRowIds(item.id, open)
                  }
                >
                  <div className="grid grid-rows-2 gap-2 text-white">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <img
                          src={getWalletIcon(item.statusdescription)}
                          className="size-6"
                        />
                        <span className="font-semibold text-[#ECECFF]">
                          {localization.getString(item.statusdescription.toLowerCase())}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {canShowRevert(item) && (
                          <Button
                            onClick={() => {
                              setShowRevertModal(true);
                            }}
                            className="flex float-right font-medium ml-2 p-1 text-xs md:text-sm rounded-sm bg-[#341A00] text-[#F69C42]"
                          >
                            <img
                              src="/icons/ic-revert.svg"
                              alt="Revert"
                              className="size-4 inline mr-1"
                            />
                            {localization.revert}
                          </Button>
                        )}
                        <span>{item.amount.toFixed(2)}₹</span>
                      </div>
                    </div>

                    <div className="flex justify-between text-sm text-[#A9A9A9]">
                      <span>
                        {formatUtcToLocalDateTime(item.transactiondate)}
                      </span>
                      <span className="text-[#E6E6E6]">
                        {item.endbalance?.toFixed(2)}₹
                      </span>
                    </div>

                    {isOpen && (
                      <div className="grid gap-2 border-t border-[#353535] mt-2 pt-2 text-sm">
                        {(() => {
                          if (
                            item.isbettransaction === true &&
                            item.statusdescription !== "Casino Bet Placed" &&
                            item.statusdescription !== "Casino Bet Settled" &&
                            item.statusdescription !== "Casino Bet ReSettled" &&
                            item.statusdescription !== "Bonus Credited"
                          ) {
                            // BET ID
                            return (
                              <div className="flex justify-between text-sm text-[#A9A9A9]">
                                <span className="text-sm text-[#A9A9A9]">{localization.betid}:</span>
                                <span
                                  className="text-white flex items-center gap-1 cursor-pointer copy-content"
                                  onClick={() => handleCopy(item.refrencenumber)}
                                >
                                  {item.refrencenumber}
                                  <img src="icons/ic-copy.svg" alt="copy" />
                                </span>
                              </div>
                            );
                          }
                          else if (item.statusdescription === "Casino Bet Placed" || item.statusdescription === "Casino Bet Settled" || item.statusdescription === "Casino Bet ReSettled"
                          ) {
                            // ROUND ID
                            return (
                              <div className="flex justify-between">
                                <span>{localization.round_id}:</span>
                                <span
                                  className="text-white flex items-center gap-1 cursor-pointer"
                                  onClick={() => handleCopy(item.refrencenumber)}
                                >
                                  {item.refrencenumber}
                                  <img src="icons/ic-copy.svg" alt="copy" />
                                </span>
                              </div>
                            );
                          }
                          else {
                            // TRANSACTION ID
                            return (
                              <div className="flex justify-between">
                                <span>{localization.transactionid}:</span>
                                <span
                                  className="text-white flex items-center gap-1 cursor-pointer"
                                  onClick={() => handleCopy(item.transactionid)}
                                >
                                  {item.transactionid}
                                  <img src="icons/ic-copy.svg" alt="copy" />
                                </span>
                              </div>
                            );
                          }
                        })()}
                        <div className="flex justify-between">
                          <span>{localization.amount}:</span>
                          <span className="text-white">
                            {formatSignedAmount(item.amount)}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span>{localization.startbalance}:</span>
                          <span className="text-white">
                            {item.startbalance?.toFixed(2)}₹
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span>{localization.endbalance}:</span>
                          <span className="text-white">
                            {item.endbalance?.toFixed(2)}₹
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </TransactionCard>
              );
            })}
          </div>
        </InfiniteScroll>
      )}

      {!loading && (!data || data.details.length === 0) && (
        <div className="text-center text-white mt-6 text-[25px]">
          {localization.no_wallet_history}
        </div>
      )}
      {showRevertModal && (
        <ConfirmationModal
          isOpen={showRevertModal}
          onConfirm={() => { handleWithdrawRevert(); }}
          onCancel={() => { setShowRevertModal(false); }}
          title={localization.withdraw_revert_confirmation_label}
          description={`\u20B9${Number(lastWithdrawal?.amount)?.toFixed(2)}`}
          descriptionClassName="text-center text-[34px] font-medium"
          icon={"/icons/ic-info.svg"}
          blurColor="bg-[#FFE6CC]"
          confirmText={localization.yes}
          cancelText={localization.no}
        />
      )}
    </div>
  );
}
